'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { RouterOutputs } from '@package/api'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate, formatSecondsToHMS, getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../../context'
import { QuizQuestionDisplay } from '../../../../core/quiz-player/question'

type ScoreboardItem = RouterOutputs['lesson']['getScoreboardData']['items'][number]

interface ReviewQuizModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lessonId: string
  learnEnrollmentId: string
  childProfile: ScoreboardItem['child']
}

export function ReviewQuizModal({
  open,
  onOpenChange,
  lessonId,
  learnEnrollmentId,
  childProfile,
}: ReviewQuizModalProps) {
  const trpc = useSharedTRPC()

  const enabled = open

  const {
    data: quizLesson,
    isPending: isLessonPending,
    isError: isLessonError,
    refetch: refetchLesson,
  } = useQuery(trpc.lesson.getQuizLesson.queryOptions({ lessonId }, { enabled }))

  const {
    data: attemptData,
    isPending: isAttemptPending,
    isError: isAttemptError,
    refetch: refetchAttempt,
  } = useQuery(trpc.lesson.getScoreboardQuizAttempt.queryOptions({ lessonId, learnEnrollmentId }, { enabled }))

  const answersByQuestionId = useMemo(() => {
    const map = new Map<string, { questionId: string; selectedIndex: number }>()
    for (const a of attemptData?.attempt?.answers ?? []) {
      map.set(a.questionId, a)
    }
    return map
  }, [attemptData])

  const isPending = isLessonPending || isAttemptPending
  const isError = isLessonError || isAttemptError

  const attempt = attemptData?.attempt
  const questions = quizLesson?.questions ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:max-w-2xl srd:h-[90dvh] srd:overflow-hidden'>
        {/* Dark title strip */}
        <DialogHeader>
          <DialogTitle>Tinjau Hasil Kuis</DialogTitle>
          <DialogDescription>{quizLesson?.title ?? ''}</DialogDescription>
        </DialogHeader>

        {/* White profile + stats section */}
        <div className='srd:flex srd:flex-col srd:gap-y-4'>
          <div className='srd:flex srd:items-center srd:gap-x-2'>
            <Avatar className='srd:size-10 srd:shrink-0'>
              <AvatarImage src={childProfile.image ?? undefined} />
              <AvatarFallback>{getInitials(childProfile.name)}</AvatarFallback>
            </Avatar>
            <p className={cn(typographyVariants({ variant: 'body-lg' }))}>{childProfile.name}</p>
          </div>

          {attempt && (
            <div className='srd:flex srd:flex-col srd:gap-y-1'>
              <StatRow
                label='Jawaban benar'
                value={`${attempt.correctCount ?? 0} dari ${attempt.totalQuestions ?? questions.length} soal`}
              />
              <StatRow label='Tanggal' value={formatDate(attempt.completedAt, { dateStyle: 'long' })} />
              <StatRow label='Durasi' value={formatSecondsToHMS(attempt.durationSeconds ?? 0, '2')} />
              <StatRow label='Percobaan ke' value={String(attempt.attemptCount ?? 1)} />
            </div>
          )}
        </div>

        {/* Scrollable questions */}
        <div className='srd:flex-1 srd:overflow-y-auto'>
          {isPending && (
            <div className='srd:flex srd:items-center srd:justify-center srd:py-16'>
              <div className='srd:size-8 srd:rounded-full srd:border-4 srd:border-neutral-black srd:border-t-transparent srd:animate-spin' />
            </div>
          )}

          {isError && !isPending && (
            <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:py-16 srd:gap-y-4 srd:text-neutral-black'>
              <p className={cn(typographyVariants({ variant: 'body-md' }))}>Gagal memuat data kuis</p>
              <Button
                variant='outline'
                onClick={() => {
                  void Promise.all([refetchLesson(), refetchAttempt()])
                }}
                className='srd:w-fit'
              >
                Coba lagi
              </Button>
            </div>
          )}

          {!isPending && !isError && (
            <div className='srd:flex srd:flex-col srd:gap-y-4'>
              {questions.map((question, index) => (
                <div key={question.id} className='srd:px-4 srd:py-8 srd:bg-neutral-black srd:rounded-2xl'>
                  <p
                    className={cn(
                      typographyVariants({ variant: 'title-lg' }),
                      'srd:text-grey-100 srd:text-center srd:mb-4',
                    )}
                  >
                    Soal {index + 1} dari {questions.length}
                  </p>
                  <QuizQuestionDisplay
                    question={question}
                    questionIndex={index}
                    mode='review'
                    studentPreviewActive={false}
                    selectedIndex={null}
                    submittedIndex={null}
                    reviewAnswer={answersByQuestionId.get(question.id)}
                    onSelectOption={() => {
                      /* read-only */
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} className='srd:w-full'>
            Tutup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='srd:flex srd:items-baseline srd:gap-x-1'>
      <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:font-bold')}>{label}:</span>
      <span className={cn(typographyVariants({ variant: 'body-sm' }))}>{value}</span>
    </div>
  )
}
